import React, { useState, useEffect } from 'react'
import NavBar from '../component/NavBar'

// Sample gallery data - 20 images and 5 videos
const galleryImages = [
  { id: 1, src: '/c1.jpg', alt: 'Community Health Initiative', category: 'Health' },
  { id: 2, src: '/c2.jpg', alt: 'Education Program', category: 'Education' },
  { id: 3, src: '/c3.jpg', alt: 'Food Distribution', category: 'Community' },
  { id: 4, src: '/c4.jpg', alt: 'Medical Camp', category: 'Health' },
  { id: 5, src: '/c5.jpg', alt: 'School Supplies Drive', category: 'Education' },
  { id: 6, src: '/covid1.jpg', alt: 'Clean Water Project', category: 'Community' },
  { id: 7, src: '/covid2.jpg', alt: 'Women\'s Empowerment', category: 'Community' },
  { id: 8, src: '/covid3.jpg', alt: 'Child Welfare', category: 'Community' },
  { id: 9, src: '/covid4.jpg', alt: 'Rural Development', category: 'Community' },
  { id: 10, src: '/covid5.jpg', alt: 'Health Awareness', category: 'Health' },
  { id: 11, src: '/covid6.jpg', alt: 'Vocational Training', category: 'Education' },
  { id: 12, src: '/covid7.jpg', alt: 'Elderly Care', category: 'Community' },
  { id: 13, src: '/covid8.jpg', alt: 'Vaccination Drive', category: 'Health' },
  { id: 14, src: '/covid9.jpg', alt: 'Library Setup', category: 'Education' },
  { id: 15, src: '/covid10.jpg', alt: 'Emergency Relief', category: 'Community' },
  { id: 16, src: '/covid11.jpg', alt: 'Nutrition Program', category: 'Health' },
  { id: 17, src: '/covid12.jpg', alt: 'Skill Development', category: 'Education' },
  { id: 18, src: '/covid13.jpg', alt: 'Environmental Initiative', category: 'Community' },
  { id: 19, src: '/covid14.jpg', alt: 'Healthcare Workshop', category: 'Health' },
  { id: 20, src: '/covid15.jpg', alt: 'Community Gathering', category: 'Community' },
]

const galleryVideos = [
  { id: 1, title: 'Annual Report 2024', duration: '5:32', thumbnail: 'https://picsum.photos/seed/video1/800/450' },
  { id: 2, title: 'Impact Stories', duration: '8:15', thumbnail: 'https://picsum.photos/seed/video2/800/450' },
  { id: 3, title: 'Community Highlights', duration: '4:45', thumbnail: 'https://picsum.photos/seed/video3/800/450' },
  { id: 4, title: 'Education Initiative', duration: '6:20', thumbnail: 'https://picsum.photos/seed/video4/800/450' },
  { id: 5, title: 'Healthcare Mission', duration: '7:10', thumbnail: 'https://picsum.photos/seed/video5/800/450' },
]

const categories = ['All', 'Health', 'Education', 'Community']

function Gallery() {
  const [selectedImage, setSelectedImage] = useState(null)
  const [activeCategory, setActiveCategory] = useState('All')
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)

  // Filter images based on category
  const filteredImages = activeCategory === 'All' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory)

  // Handle image click
  const handleImageClick = (image) => {
    setSelectedImage(image)
    setIsLightboxOpen(true)
  }

  // Handle lightbox close
  const closeLightbox = () => {
    setIsLightboxOpen(false)
    setSelectedImage(null)
  }

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isLightboxOpen) return
      if (e.key === 'Escape') closeLightbox()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isLightboxOpen])

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-gray-800 to-gray-900 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Gallery
          </h1>
          <p className="text-xl md:text-2xl mb-4 opacity-90">
            Capturing moments of compassion and care
          </p>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
            Our Photo Gallery
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-8">
            Browse through our collection of photos showcasing the work we do and the lives we touch. 
            These moments reflect the spirit of compassion that drives Shehala Foundation.
          </p>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 rounded-full transition-all duration-300 font-medium ${
                  activeCategory === category
                    ? 'bg-blue-600 text-white shadow-lg transform scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Photo Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredImages.map((image) => (
              <div
                key={image.id}
                className="group relative overflow-hidden rounded-lg shadow-md cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-xl"
                onClick={() => handleImageClick(image)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white font-medium text-lg">{image.alt}</p>
                    <span className="text-blue-300 text-sm">{image.category}</span>
                  </div>
                </div>
                <div className="absolute top-3 right-3 bg-blue-600 text-white text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {image.category}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Gallery Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
            Video Gallery
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-10">
            Watch our videos to see the real impact of our work and hear inspiring stories from the communities we serve.
          </p>

          {/* Video Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {galleryVideos.map((video) => (
              <div
                key={video.id}
                className="group bg-white rounded-lg shadow-md overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-xl"
              >
                <div className="relative">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-48 object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center transform scale-0 group-hover:scale-100 transition-transform duration-300">
                      <svg className="w-8 h-8 text-blue-600 ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white text-sm px-2 py-1 rounded">
                    {video.duration}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-gray-800 text-lg group-hover:text-blue-600 transition-colors duration-300">
                    {video.title}
                  </h3>
                  <p className="text-gray-500 text-sm mt-1">
                    Watch now
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {isLightboxOpen && selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={closeLightbox}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
            />
            <div className="text-center mt-4">
              <h3 className="text-white text-xl font-semibold">{selectedImage.alt}</h3>
              <p className="text-blue-400">{selectedImage.category}</p>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="opacity-80">
            © {new Date().getFullYear()} Shehala Foundation. All rights reserved.
          </p>
          <p className="opacity-60 text-sm mt-2">
            A Compassion-Driven Approach to Corporate Social Responsibility
          </p>
        </div>
      </footer>
    </div>
  )
}

export default Gallery
